import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectIdeaBaseWizardComponent } from '@app/components/project-idea-base-wizard/project-idea-base-wizard.component';
import { resolveProjectStatusIds } from '@app/helpers/project-state.helper';
import { ProjectAdminResponse } from '@app/models/admin.model';
import { BaseEnumResponse } from '@app/models/shared.models';
import { ProjectAdminService } from '@app/services/admin/project-admin.service';
import { ProjectSetupService } from '@app/services/project-setup/project-setup.service';
import { ToastService } from '@app/services/toast.service';
import { GenericProjectStore } from '@app/stores/projects/generic-project.store';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { DetailsPageHeaderComponent } from '@app/ui-components/details-page-header/details-page-header.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize, map, startWith } from 'rxjs';

@Component({
  selector: 'app-project-ideas-edit',
  standalone: true,
  imports: [
    CommonModule,
    DetailsPageHeaderComponent,
    ButtonComponent,
    ProjectIdeaBaseWizardComponent,
    TranslateModule,
  ],
  templateUrl: './project-idea-edit.component.html',
  styleUrl: './project-idea-edit.component.scss',
})
export class ProjectIdeaEditComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly projectService = inject(ProjectAdminService);
  private readonly projectSetupService = inject(ProjectSetupService);
  private readonly genericStore = inject(GenericProjectStore);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);

  readonly isLoading = signal(true);
  readonly isUpdating = signal(false);

  readonly projectId = toSignal(
    this.route.paramMap.pipe(
      startWith(this.route.snapshot.paramMap),
      map((param) => param.get('id') ?? ''),
    ),
    { initialValue: '' },
  );

  projectName = '';
  readonly project = signal<ProjectAdminResponse | null>(null);

  readonly projectStatuses = computed<BaseEnumResponse[]>(() =>
    this.genericStore.projectStatusEntities(),
  );
  readonly statusIds = computed(() =>
    resolveProjectStatusIds(this.projectStatuses()),
  );

  // Determines whether the project can still be accepted or declined
  // Any negative (e.g. Rejected) or higher state values are considered finalized and cannot be moderated
  readonly canModerate = computed<boolean>(() => {
    const state = this.project()?.state;
    const ids = this.statusIds();

    if (state == null) return false;
    return state >= ids.Idea && state < ids.AnalysedByStaff;
  });

  ngOnInit(): void {
    const id = this.projectId().trim();
    if (!id) {
      this.isLoading.set(false);
      return;
    }
    this.isLoading.set(true);
    this.projectService
      .getProjectAdminById(id)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (project: ProjectAdminResponse) => {
          this.project.set(project);
          this.projectName = (project?.name ?? '').toString().trim();
        },
        error: () => {
          this.project.set(null);
          this.projectName = '';
        },
      });
  }

  onBack(): void {
    this.router.navigate(['/admin/project-ideas', this.projectId()]);
  }

  decline(): void {
    const id = this.projectId().trim();
    if (!id) return;
    this.isUpdating.set(true);
    this.projectService
      .updateProjectStateToRefuse(id)
      .pipe(finalize(() => this.isUpdating.set(false)))
      .subscribe({
        next: () => {
          this.toast.success(
            this.translate.instant('admin.projectIdeas.edit.toasts.declined'),
          );
          this.onBack();
        },
        error: () =>
          this.toast.error(
            this.translate.instant(
              'admin.projectIdeas.edit.toasts.declineFailed',
            ),
          ),
      });
  }

  accept(): void {
    const id = this.projectId().trim();
    if (!id) return;
    this.isUpdating.set(true);
    this.projectSetupService
      .statusToDraft(id)
      .pipe(finalize(() => this.isUpdating.set(false)))
      .subscribe({
        next: () => {
          this.toast.success(
            this.translate.instant('admin.projectIdeas.edit.toasts.accepted'),
          );
          this.onBack();
        },
        error: () =>
          this.toast.error(
            this.translate.instant(
              'admin.projectIdeas.edit.toasts.acceptFailed',
            ),
          ),
      });
  }
}
